import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import { Drawer } from '@/components/Drawer/Drawer';
import * as S from '@/components/Ad/Status/PaymentFlow.styles';
import * as SPromotion from '@/components/Ad/Promotions/Promotion.styles';
import * as SpromotionFlow from '@/components/Ad/Promotions/PromotionFlow.styles';
import { formatNumber, makeHrefForPhone } from '@/utils/helpers';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { AngleBackIcon } from '@/components/UI/Icons/Icons';
import { CONTACT_US } from '@/constants/contactUs';
import { useAdDetails } from '@/hooks/useAdDetails';
import {
  RestoredAdPopupRef,
  RestoredAdPopupWithRef,
} from '@/components/Ad/Status/RestoredAdPopup';
import { getVariants } from '@/components/Ad/Status/PaymentFlow.constants';
import {
  findMinPricePerAd,
  mapVariantTypeToAdKindOrNull,
} from '@/components/Ad/Status/PaymentFlow.helpers';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { AdStatus } from '@/types';
import { useCurrentCategoryDetails } from '@/hooks/useCurrentCategory/useCurrentCategoryDetails';

const PromotionFlow = dynamic(
  () =>
    import('@/components/Ad/Promotions/PromotionFlow').then(
      mod => mod.PromotionFlow
    ),
  { ssr: false }
);

interface PaymentFlowProps {
  isOpen: boolean;
  close: () => void;
}

export const PaymentFlow: React.FC<PaymentFlowProps> = ({ isOpen, close }) => {
  const { t } = useTranslation();
  const [isPaymentDrawerOpen, togglePaymentDrawer] = useToggleOpenState();
  const restoredAdPopupRef = useRef<RestoredAdPopupRef>(null);

  const { categoryDetails } = useCurrentCategoryDetails();

  const { adDetails } = useAdDetails();

  const isSingleBundle = categoryDetails?.bundles.length === 1;
  const minBundlePrice = findMinPricePerAd(categoryDetails?.bundles);
  const minVipPrice = adDetails?.vipServices[0].price || 0;
  const minFeaturedPrice = adDetails?.featuredServices[0].price || 0;
  const prevVipPrice = adDetails?.vipServices[0].previousPrice || 0;
  const prevFeaturedPrice = adDetails?.featuredServices[0].previousPrice || 0;
  const variants = getVariants({
    t,
    minBundlePrice,
    minVipPrice,
    minFeaturedPrice,
    prevVipPrice,
    prevFeaturedPrice,
  });

  const [selectedVariant, setSelectedVariant] = useState(variants[0]);

  function getPrice(price: number) {
    return t(`pages.ad.price_from${isSingleBundle ? '_single' : ''}`, {
      price: formatNumber(price, 2),
    });
  }

  const handleClose = () => {
    togglePaymentDrawer(false);
    close();

    if (
      selectedVariant.type === 'bundle' &&
      adDetails?.status === AdStatus.Expired
    ) {
      restoredAdPopupRef.current?.toggle();
    }
  };

  return (
    <>
      <Drawer
        dataTestId={AdPageTestIds.paymentFlowDrawer.drawer}
        isOpen={isOpen}
        close={close}
        hasBottomBorder={false}
        hasBottomMargin={false}
      >
        <S.Box>
          <S.Title>{t('pages.ad.ad_for_30_days')}</S.Title>
          <S.Subtitle $variant="1">
            {t('pages.ad.limit_in_category', {
              categoryName: categoryDetails?.name,
            })}
          </S.Subtitle>

          {variants.map(variant => {
            const isSelected = variant.id === selectedVariant.id;
            const isBundle = variant.type === 'bundle';
            const prevPrice = variant.previousPrice;

            return (
              <S.Variant
                key={variant.id}
                onChangeHook={() => setSelectedVariant(variant)}
                customContent={
                  <>
                    <S.VariantIconBox>{variant.icon}</S.VariantIconBox>
                    <S.VariantTexts>
                      <S.VariantTitle
                        $variant="3"
                        $isWhite={!isBundle && isSelected}
                      >
                        {variant.title}
                      </S.VariantTitle>
                      {prevPrice ? (
                        <S.MultiplePrices>
                          <SpromotionFlow.NewPrice
                            $variant="1"
                            $isWhite={isSelected}
                          >
                            {getPrice(variant.price)}
                          </SpromotionFlow.NewPrice>

                          <SPromotion.PrevPrice $isWhite={isSelected}>
                            {getPrice(prevPrice)}
                          </SPromotion.PrevPrice>
                        </S.MultiplePrices>
                      ) : (
                        <S.VariantPrice
                          $variant="1"
                          $isWhite={!isBundle && isSelected}
                        >
                          {getPrice(variant.price)}
                        </S.VariantPrice>
                      )}
                    </S.VariantTexts>
                  </>
                }
                value={isSelected}
                appearance="radio"
                size="md"
                color="primary"
                $isActive={isSelected}
                $type={variant.type}
              />
            );
          })}

          <S.ContinueButton
            data-testid={AdPageTestIds.paymentFlowDrawer.continueButton}
            appearance="primary"
            isFullWidth
            onClick={togglePaymentDrawer}
          >
            {t('common.continue')}
          </S.ContinueButton>

          <S.NeedMoreInfo $variant="1">
            {t('pages.ad.need_more_ads_call')}
            <a href={makeHrefForPhone(CONTACT_US.PHONE)}>{CONTACT_US.PHONE}</a>
          </S.NeedMoreInfo>
        </S.Box>
      </Drawer>

      <PromotionFlow
        dataTestId={AdPageTestIds.paymentFlowDrawer.promotionFlow}
        isOpen={isPaymentDrawerOpen}
        toggle={togglePaymentDrawer}
        selectedVariant={selectedVariant}
        type={mapVariantTypeToAdKindOrNull(selectedVariant.type)}
        drawerCloseIcon={<AngleBackIcon />}
        onSuccessPayment={handleClose}
        {...(isSingleBundle && { minBundlePrice })}
      />

      <RestoredAdPopupWithRef
        ref={restoredAdPopupRef}
        dataTestId={AdPageTestIds.paymentFlowDrawer.restoredAdPopup}
      />
    </>
  );
};
